import { useState, useEffect, useRef } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { SpotSearchResult } from "@/common/model/spotimages/SpotImage";
import { SpotImageService } from "@/services/SpotImageService";

interface SpotSearchInputProps {
  onSpotSelect: (spot: SpotSearchResult) => void;
  selectedSpot: SpotSearchResult | null;
}

const SpotSearchInput: React.FC<SpotSearchInputProps> = ({
  onSpotSelect,
  selectedSpot,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SpotSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const searchSpots = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const spots = await SpotImageService.searchSpots(query);
        setResults(spots);
        setShowDropdown(true);
      } catch (error) {
        console.error("Error searching spots:", error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchSpots, 300);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  const handleSelect = (spot: SpotSearchResult) => {
    onSpotSelect(spot);
    setQuery("");
    setShowDropdown(false);
  };

  return (
    <div className="relative w-full max-w-md" ref={containerRef}>
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder={selectedSpot ? selectedSpot.name : "Search for a spot..."}
          className="block w-full rounded-md border border-gray-300 bg-white pl-10 pr-10 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
          </div>
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {results.map((spot) => (
            <button
              key={spot.id}
              onClick={() => handleSelect(spot)}
              className="w-full px-4 py-2.5 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="font-medium text-gray-900">{spot.name}</div>
              <div className="text-sm text-gray-500">
                {spot.countryName} ({spot.alpha2})
              </div>
            </button>
          ))}
        </div>
      )}

      {showDropdown && query.length >= 2 && results.length === 0 && !isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4 text-center text-gray-500">
          No spots found
        </div>
      )}
    </div>
  );
};

export default SpotSearchInput;
